import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, ButtonGroup, Table } from 'react-bootstrap';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { SummaryGranularity } from '../api/types';
import { useAsync } from '../hooks/useAsync';
import { loadVolumeSummary } from '../utils/volumeCache';
import {
  GRANULARITIES,
  GRANULARITY_CONFIG,
  METRIC_LABELS,
  VolumeMetric,
  VolumeRow,
  formatBucketPace,
  formatBucketTime,
  formatDayIso,
  periodEnd,
  toVolumeRows,
  visibleRange,
} from '../utils/volume';
import StatusMessage from './StatusMessage';
import VolumeBucketModal from './VolumeBucketModal';
import { numberAxis, Y_AXIS } from '../utils/axis';

const HEIGHT = 240;
const METRICS: VolumeMetric[] = ['distance', 'time', 'runs'];
const AXIS_UNITS: Record<VolumeMetric, string> = { distance: 'kilometres', time: 'hours', runs: 'number of runs' };
const AXIS = { stroke: 'var(--text-muted)', fontSize: 12 };

/** Props. `card` is the Dashboard and Fitness version with a title link to /volume. `page` is the full /volume page with a table. */
export interface DistanceVolumeChartProps {
  variant?: 'card' | 'page';
}

const clamp = (value: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, value));

/**
 * Running volume per week, month or year, from GET /activities/summary (running activities only).
 * The chart keeps a fixed bar width and scrolls sideways; the default view is the newest window.
 * "Older" and "Newer" move the window by one window of buckets.
 */
const DistanceVolumeChart: React.FC<DistanceVolumeChartProps> = ({ variant = 'card' }) => {
  const [granularity, setGranularity] = useState<SummaryGranularity>('week');
  const [metric, setMetric] = useState<VolumeMetric>('distance');
  const [shift, setShift] = useState(0);
  const [selected, setSelected] = useState<VolumeRow | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  const summary = useAsync(() => loadVolumeSummary(granularity), [granularity]);
  // Until the new granularity has loaded, the previous granularity's buckets are not shown.
  const ready = summary.data?.granularity === granularity ? summary.data : null;

  const config = GRANULARITY_CONFIG[granularity];
  const rows = useMemo(() => (ready ? toVolumeRows(granularity, ready.buckets, metric) : []), [ready, granularity, metric]);
  const total = rows.length;
  const maxShift = Math.max(0, total - config.windowBuckets);
  const currentShift = clamp(shift, 0, maxShift);
  const visible = visibleRange(total, config.windowBuckets, currentShift);
  const windowPx = config.windowBuckets * config.slotPx;
  const contentPx = Math.max(total, 1) * config.slotPx + 16;
  const hasRuns = rows.some(r => r.runs > 0);
  const yAxis = numberAxis(0, Math.max(1, ...rows.map(r => r.value)), 6);

  // Start at the newest end when the data arrives or the granularity changes.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [total, granularity]);

  const goTo = (next: number) => {
    const target = clamp(next, 0, maxShift);
    setShift(target);
    const el = scroller.current;
    if (el) el.scrollLeft = el.scrollWidth - el.clientWidth - target * config.slotPx;
  };

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const fromNewest = el.scrollWidth - el.clientWidth - el.scrollLeft;
    const next = clamp(Math.round(fromNewest / config.slotPx), 0, maxShift);
    if (next !== currentShift) setShift(next);
  };

  const changeGranularity = (next: SummaryGranularity) => {
    setShift(0);
    setGranularity(next);
  };

  const shownFrom = rows[visible.start];
  const shownTo = rows[visible.end - 1];
  const caption = shownFrom && shownTo
    ? `${formatDayIso(shownFrom.bucketStart)} to ${formatDayIso(periodEnd(granularity, shownTo.bucketStart))}`
    : '';

  const controls = (
    <div className="volume-controls d-flex flex-wrap align-items-center gap-2 mb-2">
      <ButtonGroup size="sm" aria-label="Period">
        {GRANULARITIES.map(g => (
          <Button
            key={g}
            variant={g === granularity ? 'primary' : 'outline-secondary'}
            aria-pressed={g === granularity}
            onClick={() => changeGranularity(g)}
          >
            {GRANULARITY_CONFIG[g].label}
          </Button>
        ))}
      </ButtonGroup>
      <ButtonGroup size="sm" aria-label="Metric">
        {METRICS.map(m => (
          <Button
            key={m}
            variant={m === metric ? 'primary' : 'outline-secondary'}
            aria-pressed={m === metric}
            onClick={() => setMetric(m)}
          >
            {METRIC_LABELS[m]}
          </Button>
        ))}
      </ButtonGroup>
    </div>
  );

  const body = (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" disabled={currentShift >= maxShift} onClick={() => goTo(currentShift + config.windowBuckets)}>
            <span aria-hidden="true">‹</span> Older
          </Button>
          <Button variant="outline-secondary" size="sm" disabled={currentShift === 0} onClick={() => goTo(currentShift - config.windowBuckets)}>
            Newer <span aria-hidden="true">›</span>
          </Button>
        </div>
        <span className="small muted tabular" aria-live="polite">{caption}</span>
      </div>

      <StatusMessage loading={summary.loading && !ready} error={summary.error} onRetry={summary.reload} />

      {ready && total > 0 && (
        <div
          className="volume-scroll"
          ref={scroller}
          onScroll={onScroll}
          style={{ maxWidth: windowPx }}
          role="region"
          aria-label={`${GRANULARITY_CONFIG[granularity].label} ${METRIC_LABELS[metric].toLowerCase()} chart`}
          tabIndex={0}
        >
          <BarChart width={contentPx} height={HEIGHT} data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
            <XAxis dataKey="label" {...AXIS} interval="preserveStartEnd" minTickGap={12} />
            <YAxis {...Y_AXIS} orientation="right" width={40} domain={yAxis.domain} ticks={yAxis.ticks} interval={0} tickFormatter={v => String(v)} />
            <Tooltip cursor={{ fill: 'var(--surface-2)' }} content={props => <VolumeTooltip {...props} />} />
            <Bar
              dataKey="value"
              fill="var(--chart-1)"
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
              cursor="pointer"
              onClick={(item: { payload?: VolumeRow }) => item.payload && setSelected(item.payload)}
            />
          </BarChart>
        </div>
      )}

      {ready && total > 0 && !hasRuns && <p className="muted small mt-2 mb-0">No runs in this range yet.</p>}
      {ready && total > 0 && (
        <p className="muted small mt-2 mb-0">Select a bar to list its runs. Drag or swipe the chart to see older periods. Vertical axis: {AXIS_UNITS[metric]}.</p>
      )}
    </>
  );

  const bucketModal = selected && ready && (
    <VolumeBucketModal
      key={`${granularity}|${selected.bucketStart}`}
      granularity={granularity}
      row={selected}
      types={ready.types}
      onHide={() => setSelected(null)}
    />
  );

  if (variant === 'page') {
    return (
      <>
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="badge text-bg-light border small">Running only</span>
        </div>
        {controls}
        {body}
        {ready && total > 0 && <BucketTable rows={[...rows].reverse()} onOpen={setSelected} />}
        {bucketModal}
      </>
    );
  }

  return (
    <section className="surface">
      <div className="d-flex justify-content-between align-items-baseline gap-2 flex-wrap mb-2">
        <h2 className="mb-0">
          <Link to="/volume" className="history-title-link">Training volume</Link>
        </h2>
        <span className="small muted">Running only</span>
      </div>
      {controls}
      {body}
      {bucketModal}
    </section>
  );
};

/** Tooltip body: distance, runs, time and average pace for the hovered bucket. */
const VolumeTooltip: React.FC<{ active?: boolean; payload?: ReadonlyArray<{ payload?: unknown }> }> = ({ active, payload }) => {
  const row = payload?.[0]?.payload as VolumeRow | undefined;
  if (!active || !row) return null;
  return (
    <div className="volume-tooltip">
      <div className="fw-semibold mb-1">{row.title}</div>
      <div>Distance: {row.distanceKm.toFixed(1)} km</div>
      <div>Runs: {row.runs}</div>
      <div>Time: {formatBucketTime(row.durationS)}</div>
      <div>Avg pace: {formatBucketPace(row.avgPaceSPerKm)}</div>
    </div>
  );
};

/** Table of every bucket in the fetched range, newest first. Each row opens the bucket's runs. */
const BucketTable: React.FC<{ rows: VolumeRow[]; onOpen: (row: VolumeRow) => void }> = ({ rows, onOpen }) => (
  <div className="table-responsive mt-3">
    <Table size="sm" className="trainer-table">
      <thead>
        <tr>
          <th>Period</th>
          <th className="text-end">Runs</th>
          <th className="text-end">Distance</th>
          <th className="text-end">Time</th>
          <th className="text-end">Avg pace</th>
          <th className="text-end">Elevation</th>
          <th className="text-end"><span className="visually-hidden">Runs in period</span></th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.bucketStart}>
            <td>{row.title}</td>
            <td className="text-end tabular">{row.runs}</td>
            <td className="text-end tabular">{row.distanceKm.toFixed(1)} km</td>
            <td className="text-end tabular">{formatBucketTime(row.durationS)}</td>
            <td className="text-end tabular">{formatBucketPace(row.avgPaceSPerKm)}</td>
            <td className="text-end tabular">{row.elevationGainM == null ? '—' : `${Math.round(row.elevationGainM)} m`}</td>
            <td className="text-end">
              <Button variant="link" size="sm" className="p-0" onClick={() => onOpen(row)} aria-label={`Runs in ${row.title}`}>
                Runs
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
);

export default DistanceVolumeChart;
